import React from 'react'
import { twMerge } from 'tailwind-merge'

import {
    Drawer as GluestackDrawer,
    type DrawerAnchor,
    DrawerBackdrop,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
} from '../../_gluestack/drawer'
import { CloseIcon } from '../../icons'
import { Box } from '../box'
import { HStack } from '../hstack'
import { Icon, type IconSource } from '../icon'
import { Pressable } from '../pressable'
import { Text } from '../text'

export type { DrawerAnchor } from '../../_gluestack/drawer'

// Re-export the tokenised primitives so a caller can compose a bespoke drawer.
export { DrawerBackdrop, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader }

export interface SideDrawerProps {
    isOpen: boolean
    onClose: () => void
    /** Which edge the panel slides from. Defaults to the right. */
    anchor?: Extract<DrawerAnchor, 'left' | 'right'>
    /** Header title (rendered H1-Regular 18, `content/default`). */
    title?: React.ReactNode
    /** Right-aligned header controls (e.g. pin / settings). The close (X) is appended automatically. */
    headerActions?: React.ReactNode
    /** Fixed panel width, e.g. `w-[480px]`. Overrides the default. */
    widthClassName?: string
    /** Extra classes on the content surface (padding is already reset to 0). */
    contentClassName?: string
    children: React.ReactNode
}

const CloseGlyph = CloseIcon as IconSource

/**
 * The app-standard side panel: a gluestack Drawer (scrim + slide-in +
 * backdrop-dismiss) tokenised to the AOS surface, with the Figma
 * Notifications/Messaging header (title + action icons + close). Replaces the
 * legacy `aos-ui` `SideContainer`. The *pinned* (always-visible, no-scrim)
 * notifications mode is NOT a modal — render that inline, not via this component.
 */
export const SideDrawer: React.FC<SideDrawerProps> = ({
    isOpen,
    onClose,
    anchor = 'right',
    title,
    headerActions,
    widthClassName = 'w-[480px]',
    contentClassName,
    children,
}) => (
    <GluestackDrawer isOpen={isOpen} onClose={onClose} anchor={anchor} size='full'>
        <DrawerBackdrop
            className='bg-black/40 [backdrop-filter:blur(12px)]'
            animate={{ opacity: 1 }}
        />
        <DrawerContent
            className={twMerge(
                'max-w-full border-0 bg-background-sub-surface p-0 shadow-none',
                anchor === 'right'
                    ? 'border-l border-border-subtle border-solid'
                    : 'border-r border-border-subtle border-solid',
                widthClassName,
                contentClassName,
            )}
        >
            <DrawerHeader className='shrink-0 border-b border-border-subtle border-solid bg-background-sub-surface px-6 py-4'>
                {typeof title === 'string' ? (
                    <Text
                        weight='regular'
                        className='text-content-default text-[18px] leading-[25px]'
                    >
                        {title}
                    </Text>
                ) : (
                    title
                )}
                <HStack className='items-center gap-2'>
                    {headerActions}
                    <Pressable
                        onPress={onClose}
                        accessibilityRole='button'
                        accessibilityLabel='Close'
                    >
                        <Icon as={CloseGlyph} size={24} className='text-content-default' />
                    </Pressable>
                </HStack>
            </DrawerHeader>
            <Box className='min-h-0 flex-1'>{children}</Box>
        </DrawerContent>
    </GluestackDrawer>
)

SideDrawer.displayName = 'SideDrawer'
